// shared-components/features/mailinglist/SimpleMailingListWidget.jsx
import { useState } from "react";
import PropTypes from "prop-types";
import { Mail } from "lucide-react";
import Button from "@shared/components/base/Button";
import { useSiteGradients } from "@shared/hooks";
import { useWebsite } from "@shared/hooks";

function SimpleMailingListWidget({
  variant = "default", // 'default' or 'compact'
  listName = "updates",
  domain,
  productionDomain,
  title = "Stay Updated",
  description = "Get email updates about tour dates, new releases, and special announcements",
}) {
  const gradients = useSiteGradients();
  const [showForm, setShowForm] = useState(false);
  const websiteContext = useWebsite();

  const getActualDomain = () => {
    if (domain) return domain; // Explicitly provided
    if (productionDomain && import.meta.env.PROD) return productionDomain; // Production only
    if (import.meta.env.DEV) return productionDomain || "example.com"; // Dev fallback
    if (websiteContext?.domain) return websiteContext.domain; // Use from context
    return window.location.hostname; // Last resort
  };

  const actualDomain = getActualDomain();
  const subscribeUrl = `https://${actualDomain}/mailman/listinfo/${listName}_${actualDomain}`;

  // In dev, maybe show a note
  if (import.meta.env.DEV && !domain && !productionDomain) {
    console.warn("MailingListWidget: No domain specified, using fallback");
  }
  // Compact version for footer
  if (variant === "compact") {
    return (
      <div className="mailing-list-compact">
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {title}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Tour updates & new releases
            </p>
          </div>
          <Button
            size="sm"
            as="a"
            href={subscribeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0"
          >
            Subscribe
          </Button>
        </div>
      </div>
    );
  }

  if (!showForm) {
    return (
      <div
        className={`${gradients.announcementsInfo} mailing-list-widget p-4 rounded-lg border border-gray-200 dark:border-gray-700`}
      >
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {description}
            </p>
            <Button
              size="sm"
              className="mt-3"
              onClick={() => setShowForm(true)}
            >
              Subscribe to Updates
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mailing-list-widget p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        You&apos;ll be redirected to our secure mailing list page to complete
        your subscription.
      </p>
      <div className="flex gap-2">
        <Button
          as="a"
          href={subscribeUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Continue to Subscribe
        </Button>
        <Button variant="ghost" onClick={() => setShowForm(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

SimpleMailingListWidget.propTypes = {
  variant: PropTypes.oneOf(["default", "compact"]),
  listName: PropTypes.string,
  domain: PropTypes.string,
  productionDomain: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default SimpleMailingListWidget;
